import React, { useRef, useState } from 'react'
import imageCompression from 'browser-image-compression'

const MAX = 5
const COMPRESS_OPTS = { maxSizeMB: 0.8, maxWidthOrHeight: 1280, useWebWorker: true, fileType: 'image/jpeg' }

// Multi-image picker with client-side compression and previews.
// `images` is an array of File objects (compressed), reported to the parent via onChange.
export default function ImagePicker({ images, onChange, disabled = false }) {
  const inputRef = useRef(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const previews = images.map(f => ({
    name: f.name,
    url: URL.createObjectURL(f),
    size: f.size,
  }))

  const handlePick = async (e) => {
    setError('')
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const remaining = MAX - images.length
    if (remaining <= 0) {
      setError(`อัปโหลดได้สูงสุด ${MAX} รูป`)
      e.target.value = ''
      return
    }
    const toProcess = files.slice(0, remaining)

    setBusy(true)
    try {
      const compressed = []
      for (const f of toProcess) {
        if (!/^image\//.test(f.type)) continue
        try {
          const out = await imageCompression(f, COMPRESS_OPTS)
          // Force .jpg extension so the server's name-based ext check stays consistent
          const renamed = new File([out], f.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' })
          compressed.push(renamed)
        } catch (err) {
          console.warn('Compress failed, using original', f.name, err)
          compressed.push(f)
        }
      }
      onChange([...images, ...compressed])
    } finally {
      setBusy(false)
      e.target.value = ''
    }
  }

  const remove = (idx) => onChange(images.filter((_, i) => i !== idx))

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="d-none"
        onChange={handlePick}
        disabled={disabled || busy}
      />

      <div className="row g-2">
        {previews.map((p, i) => (
          <div key={i} className="col-4 col-sm-3 col-md-2 position-relative">
            <img
              src={p.url}
              alt=""
              className="img-thumbnail w-100"
              style={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              className="btn btn-danger btn-sm position-absolute top-0 end-0 p-1 lh-1"
              style={{ transform: 'translate(25%, -25%)', borderRadius: '50%' }}
              aria-label="ลบรูป"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        ))}

        {images.length < MAX && (
          <div className="col-4 col-sm-3 col-md-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={disabled || busy}
              className="btn btn-outline-primary w-100 d-flex flex-column align-items-center justify-content-center"
              style={{ aspectRatio: '1 / 1' }}
            >
              {busy
                ? <span className="spinner-border spinner-border-sm" />
                : <><i className="ri-camera-fill fs-3" /><small>เพิ่มรูป</small></>}
            </button>
          </div>
        )}
      </div>

      <div className="form-text mt-1">
        แนบรูปได้สูงสุด {MAX} รูป (รูปจะถูกย่อขนาดอัตโนมัติก่อนอัปโหลด)
      </div>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </div>
  )
}
